import React, { useMemo, useState, useEffect } from "react";
import stylesLess from "./index.module.less";
import { useRouter } from "next/router";
import { Util, Platform, ForceMobileContext, Hooks } from "@xt/xtbase";
import Nav from "@xt/Nav";
import LandingNav from "components/Pages/Landing/LandingNav";
import Foot from "@xt/Foot";
import downLogo from "assets/images/downLogo.png";
import downAction from "assets/images/downAction.png";
import downClose from "assets/images/downClose.png";

const { getUrl } = Util;
const { useTranslation } = Hooks;

const MOBILE_SIDE = 768;

// 与 web-index 一致：仅按视口较短边（minSide > 768）控制遮罩；未拿到尺寸前不展示，避免首屏误挡
function useViewportSize() {
  const [size, setSize] = useState({ w: null, h: null });
  useEffect(() => {
    const update = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return size;
}

function getShowPcMobileMask(w, h) {
  if (w == null || h == null) return false;
  return Math.min(w, h) > MOBILE_SIDE;
}

export default function Layout({
  children,
  isShowNav = true,
  isShowFoot = false,
  style = {},
  useBottomBar = true,
  /** 仅保留底部 Home Tab，隐藏移动端顶部 NavbarMb（用于页面内自带顶栏的页面如 /convert/buy） */
  bottomBarHideTop = false,
  disablePcMobileTip = false,
  landingNav = false,
}) {
  const router = useRouter();
  const { t } = useTranslation();
  const viewportSize = useViewportSize();
  const showPcMobileMask = getShowPcMobileMask(viewportSize.w, viewportSize.h) && !disablePcMobileTip;
  const isInApp = Platform.isInApp();

  const downAppClick = () =>{
    window.location = getUrl("/app")
  }

  const [visible, setVisible] = useState(true);
  const [isAppUrl, isSpecialActivityUrl] = useMemo(() => {
    return [router.route == '/app', router.route.includes('5-years-anniversary')];
  }, [router]);

  const isHomePage = router.pathname === "/" || router.route === "/";
  const showNav = !Platform.isInApp() && (isShowNav != false);
  const showFoot = !Platform.isInApp() && (isShowFoot != false);

  /** 顶部 Navbar 仅首页展示；非首页保留 BottomBar（若启用） */
  const showTopNav = showNav && !landingNav && isHomePage && !bottomBarHideTop;
  const effectiveBottomBarHideTop =
    bottomBarHideTop || (useBottomBar && showNav && !landingNav && !isHomePage);

  // 检测是否使用了底部 bar
  // 如果传入了 useBottomBar prop，或者 Nav 组件传入了 bottomBar prop，则认为使用了底部 bar
  const hasBottomBar = useBottomBar && !landingNav;

  /**
   * 顶部占位：站点 NavbarMb 需要 mainFixedNav；仅 BottomBar 时用页面内 BackHeader 自行 padding，避免重复顶栏遮挡。
   */
  const isFixedNav =
    !isInApp &&
    (landingNav || (showNav && !(useBottomBar && effectiveBottomBarHideTop)));
  
  // 计算样式：顶部 padding 和底部 padding（用于底部 bar）
  // 注意：顶部 padding 现在由 .mainFixedNav 类统一处理，不再需要单独设置
  const styles = useMemo(() => {
    const baseStyle = {
      ...style
    };
    
    // 如果使用底部 bar，添加底部 padding（与 @xt/Nav bottom-bar 实际占位一致，约 70px + safe area）
    if (hasBottomBar) {
      baseStyle.paddingBottom = 'calc(70px + env(safe-area-inset-bottom, 0px))';
    }
    
    return baseStyle;
  }, [style, hasBottomBar]);

  const renderNav = () => {
    if (landingNav) {
      return <LandingNav />;
    }
    if (useBottomBar && effectiveBottomBarHideTop) {
      return <Nav mode="fixed" bgColor="#181a20" bottomBar bottomBarHideTop />;
    }
    if (useBottomBar) {
      return <Nav mode="fixed" bgColor="#181a20" bottomBar />;
    }
    if (showTopNav) {
      return <Nav mode="fixed" bgColor="#181a20" />;
    }
    return null;
  };

  const shouldRenderNav =
    !isInApp &&
    (landingNav || (showNav && (useBottomBar || showTopNav)));

  const content = (
    <div className={stylesLess.page} style={styles}>
      {showPcMobileMask ? (
        <div className={stylesLess.pcMobileTip}>
          <img src="https://www.spikex.com/logo2.png" alt="" className={stylesLess.pcMobileTipLogo} />
          <span>{t("common.pleaseOpenInMobileBrowser")}</span>
        </div>
      ) : null}
      <div>
        {shouldRenderNav ? renderNav() : null}
        <div
          className={`${stylesLess.main} ${
            isFixedNav
              ? landingNav
                ? stylesLess.mainFixedNavLanding
                : stylesLess.mainFixedNav
              : ""
          }`}
        >
          {children}
        </div>
        {showFoot ? <Foot /> : null}
      </div>
    </div>
  );

  if (showPcMobileMask) {
    return (
      <ForceMobileContext.Provider value={true}>
        {content}
      </ForceMobileContext.Provider>
    );
  }
  return content;
}
